import React from 'react';
import SEO from '../SEO/SEO';
import '../../assets/styles/Pages.css';

const Guide = () => {
  return (
    <div className="page-content">
      <SEO 
        title="Guia de Juros Compostos | Aprenda a Investir" 
        description="Aprenda como funcionam os juros compostos e como utilizá-los para alcançar sua independência financeira. Guia completo com exemplos práticos."
        keywords={['guia juros compostos', 'como investir', 'educação financeira', 'independência financeira']}
      />
      
      <h1>Guia de Juros Compostos</h1>
      
      <section>
        <h2>O que são Juros Compostos?</h2>
        <p>Juros compostos são o fenômeno de adicionar juros acumulados ao principal de um investimento ou empréstimo, de modo que os juros também geram juros ao longo do tempo. É frequentemente chamado de "juros sobre juros".</p>
        <p>Este conceito está no coração dos investimentos de longo prazo e é considerado uma das forças mais poderosas em finanças.</p>
      </section>
      
      <section>
        <h2>A Fórmula dos Juros Compostos</h2>
        <p>A fórmula básica para calcular juros compostos é:</p>
        <div className="formula">M = P × (1 + i)^n</div>
        <p>Onde:</p>
        <ul>
          <li><strong>M</strong> = Montante final</li>
          <li><strong>P</strong> = Principal (valor inicial)</li>
          <li><strong>i</strong> = Taxa de juros (em formato decimal)</li>
          <li><strong>n</strong> = Número de períodos</li>
        </ul>
        <p>Para incluir aportes mensais, a fórmula fica um pouco mais complexa:</p>
        <div className="formula">M = P × (1 + i)^n + PMT × [(1 + i)^n - 1] / i</div>
        <p>Onde <strong>PMT</strong> é o valor do aporte periódico.</p>
      </section>
      
      <section>
        <h2>O Poder dos Juros Compostos</h2>
        <div className="highlight">
          <p>Albert Einstein supostamente chamou os juros compostos de "a oitava maravilha do mundo" e "a maior invenção matemática da humanidade".</p>
        </div>
        <p>Embora não haja evidências históricas concretas de que ele tenha dito isso, a afirmação ilustra bem o poder transformador deste conceito.</p>
        <p>O verdadeiro poder dos juros compostos está na sua capacidade de acelerar o crescimento de um investimento ao longo do tempo. Quanto mais tempo o dinheiro fica investido, mais significativo é o efeito dos juros compostos.</p>
      </section>
      
      <section>
        <h2>A Regra dos 72</h2>
        <p>A Regra dos 72 é uma forma simples de estimar quanto tempo levará para dobrar seu dinheiro com juros compostos:</p>
        <div className="formula">Anos para dobrar = 72 ÷ Taxa de juros anual</div>
        <p>Por exemplo, com uma taxa de juros anual de 8%, levaria aproximadamente 9 anos para dobrar seu investimento (72 ÷ 8 = 9).</p>
      </section>
      
      <section>
        <h2>Fatores que Afetam os Juros Compostos</h2>
        <p>Vários fatores podem impactar significativamente o crescimento do seu investimento com juros compostos:</p>
        <ul>
          <li><strong>Taxa de juros</strong> - Quanto maior a taxa, mais rápido o crescimento</li>
          <li><strong>Tempo de investimento</strong> - Quanto mais tempo investido, maior o efeito dos juros compostos</li>
          <li><strong>Frequência de capitalização</strong> - Quanto mais frequente (diária, mensal, trimestral, anual), maior o retorno</li>
          <li><strong>Aportes adicionais</strong> - Contribuições regulares aumentam significativamente o resultado final</li>
          <li><strong>Impostos</strong> - Podem reduzir o retorno efetivo do investimento</li>
          <li><strong>Inflação</strong> - Reduz o poder de compra do dinheiro ao longo do tempo</li>
        </ul>
      </section>
      
      <section>
        <h2>Usando Nossa Calculadora</h2>
        <p>Nossa calculadora de juros compostos permite que você:</p>
        <ul>
          <li>Defina um capital inicial</li>
          <li>Estabeleça aportes mensais</li>
          <li>Defina a taxa de juros</li>
          <li>Escolha o período de investimento</li>
          <li>Visualize o crescimento do seu investimento</li>
          <li>Compare diferentes cenários</li>
          <li>Estabeleça marcos financeiros</li>
        </ul>
        <p>Use-a para planejar seu futuro financeiro e compreender melhor o poder dos juros compostos.</p>
      </section>
    </div>
  );
};

export default Guide;